// static/table_engine_src/layout/column_layout.js
import { escCss } from "../util/dom.js";

function clampWidth(v, minW, maxW) {
  const n = Number(v);
  if (!Number.isFinite(n)) return minW;
  return Math.min(maxW, Math.max(minW, Math.round(n)));
}

function widthFromCol(col) {
  if (!col) return null;
  const w = col.width;
  if (Number.isFinite(w)) return Number(w);
  if (typeof w === "string") {
    const n = parseFloat(w);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function installColumnLayout(ctx) {
  if (!ctx || typeof ctx !== "object") return;
  if (!Array.isArray(ctx.cols) || !ctx.colByKey || !ctx.hiddenCols) return;

  const spec = ctx.spec || {};
  const cols = ctx.cols;
  const hiddenCols = ctx.hiddenCols;

  const resizeEnabled = spec.columnResize !== false;
  const reorderEnabled = spec.columnReorder !== false;
  const persistEnabled = spec.layoutProfile !== false;

  const minWidth = Number.isFinite(spec.minColumnWidth)
    ? Math.max(28, Math.trunc(spec.minColumnWidth))
    : 44;
  const maxWidth = Number.isFinite(spec.maxColumnWidth)
    ? Math.max(minWidth, Math.trunc(spec.maxColumnWidth))
    : 1200;

  function clampWidthForCol(col, v) {
    const colMin = Number.isFinite(Number(col?.minWidth))
      ? Math.max(minWidth, Math.round(Number(col.minWidth)))
      : minWidth;
    const colMax0 = Number.isFinite(Number(col?.maxWidth))
      ? Math.round(Number(col.maxWidth))
      : maxWidth;
    const colMax = Math.max(colMin, Math.min(maxWidth, colMax0));
    return clampWidth(v, colMin, colMax);
  }

  const tableId = spec.tableId || spec.rootId || "table";
  const storageKey = spec.layoutStorageKey || `te.layout::${tableId}`;
  const store = (() => {
    if (!persistEnabled) return null;
    try {
      return spec.layoutStorage === "session" ? window.sessionStorage : window.localStorage;
    } catch {
      return null;
    }
  })();

  function isRowHandleKey(colKey) {
    return !!(ctx.rowMenuEnabled && colKey && colKey === ctx.ROW_HANDLE_KEY);
  }

  function colForKey(colKey) {
    return ctx.colByKey?.get?.(colKey) || null;
  }

  function pushHistoryAction(action) {
    if (!action || typeof action !== "object") return false;
    if (typeof ctx.pushHistoryAction !== "function") return false;
    return !!ctx.pushHistoryAction({
      ...action,
      ts: Number.isFinite(action.ts) ? action.ts : Date.now(),
    });
  }

  function withHistorySquelch(fn) {
    if (typeof fn !== "function") return undefined;
    const h = ctx._hist;
    if (!h || typeof h !== "object") return fn();
    const prev = !!h.squelch;
    h.squelch = true;
    try {
      return fn();
    } finally {
      h.squelch = prev;
    }
  }

  function canResizeCol(colKey) {
    if (!resizeEnabled || !colKey) return false;
    if (isRowHandleKey(colKey)) return false;

    const col = colForKey(colKey);
    if (!col) return false;
    if (col.resizable === false || col.resize === false) return false;
    return true;
  }

  function canReorderCol(colKey) {
    if (!reorderEnabled || !colKey) return false;
    if (isRowHandleKey(colKey)) return false;

    const col = colForKey(colKey);
    if (!col) return false;
    if (col.reorderable === false || col.reorder === false) return false;
    return true;
  }

  function parseLayoutProfile(rawObj) {
    const out = {
      order: [],
      widths: {},
      hidden: [],
    };

    if (!rawObj || typeof rawObj !== "object") return out;

    if (Array.isArray(rawObj.order)) {
      out.order = [...new Set(rawObj.order.map((k) => String(k || "").trim()).filter(Boolean))];
    }

    if (rawObj.widths && typeof rawObj.widths === "object") {
      for (const [k, v] of Object.entries(rawObj.widths)) {
        const key = String(k || "").trim();
        if (!key) continue;
        const col = colForKey(key);
        if (!col || isRowHandleKey(key)) continue;
        out.widths[key] = clampWidthForCol(col, v);
      }
    }

    if (Array.isArray(rawObj.hidden)) {
      out.hidden = [...new Set(rawObj.hidden.map((k) => String(k || "").trim()).filter(Boolean))]
        .filter((k) => !!colForKey(k))
        .filter((k) => !isRowHandleKey(k));
    }

    return out;
  }

  function loadLayoutProfile() {
    if (!store) return null;
    try {
      const raw = store.getItem(storageKey);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return parseLayoutProfile(obj);
    } catch {
      return null;
    }
  }

  function makeLayoutProfile() {
    const widths = {};

    for (const col of cols) {
      if (!col?.key || isRowHandleKey(col.key)) continue;
      const w = widthFromCol(col);
      if (Number.isFinite(w)) widths[col.key] = clampWidthForCol(col, w);
    }

    return {
      v: 1,
      order: cols.map((c) => c?.key).filter(Boolean),
      widths,
      hidden: [...hiddenCols].filter((k) => !isRowHandleKey(k)),
    };
  }

  function saveLayoutProfile() {
    if (!store) return false;
    try {
      store.setItem(storageKey, JSON.stringify(makeLayoutProfile()));
      return true;
    } catch {
      return false;
    }
  }

  function clearLayoutProfile() {
    if (!store) return false;
    try {
      store.removeItem(storageKey);
      return true;
    } catch {
      return false;
    }
  }

  const baselineProfile = (() => {
    const widths = {};
    cols.forEach((col) => {
      if (!col?.key || isRowHandleKey(col.key)) return;
      const w = widthFromCol(col);
      if (Number.isFinite(w)) widths[col.key] = clampWidthForCol(col, w);
    });
    return {
      order: cols.map((c) => c?.key).filter(Boolean),
      widths,
      hidden: [...hiddenCols].filter((k) => !isRowHandleKey(k)),
    };
  })();

  function reorderColumnsByRefs(orderCols) {
    if (!Array.isArray(orderCols) || !orderCols.length) return false;

    const curSet = new Set(cols);
    const next = [];
    const seen = new Set();

    orderCols.forEach((col) => {
      if (!col || !curSet.has(col) || seen.has(col)) return;
      next.push(col);
      seen.add(col);
    });

    cols.forEach((col) => {
      if (!col || seen.has(col)) return;
      next.push(col);
      seen.add(col);
    });

    if (next.length !== cols.length) return false;

    let same = true;
    for (let i = 0; i < cols.length; i++) {
      if (cols[i] !== next[i]) {
        same = false;
        break;
      }
    }
    if (same) return false;

    cols.splice(0, cols.length, ...next);
    return true;
  }

  function reorderColumnsByOrder(orderKeys) {
    if (!Array.isArray(orderKeys) || !orderKeys.length) return false;

    const byKey = new Map(cols.map((c) => [c?.key, c]));
    const next = [];

    orderKeys.forEach((key0) => {
      const key = String(key0 || "").trim();
      if (!key) return;
      const col = byKey.get(key);
      if (!col) return;
      next.push(col);
      byKey.delete(key);
    });

    cols.forEach((col) => {
      if (!col?.key) return;
      if (!byKey.has(col.key)) return;
      next.push(col);
      byKey.delete(col.key);
    });

    if (next.length !== cols.length) return false;

    let same = true;
    for (let i = 0; i < cols.length; i++) {
      if (cols[i] !== next[i]) {
        same = false;
        break;
      }
    }
    if (same) return false;

    cols.splice(0, cols.length, ...next);
    return true;
  }

  function applyLayoutProfile(profile) {
    if (!profile || typeof profile !== "object") return false;

    let changed = false;

    if (Array.isArray(profile.order) && profile.order.length) {
      if (reorderColumnsByOrder(profile.order)) changed = true;
    }

    if (profile.widths && typeof profile.widths === "object") {
      for (const [key, w0] of Object.entries(profile.widths)) {
        const col = colForKey(key);
        if (!col || isRowHandleKey(key)) continue;
        const w = clampWidthForCol(col, w0);
        const prev = widthFromCol(col);
        if (prev != null && Math.abs(prev - w) < 0.5) continue;
        col.width = w;
        changed = true;
      }
    }

    if (Array.isArray(profile.hidden)) {
      const nextHidden = new Set(
        profile.hidden.filter((k) => !!colForKey(k)).filter((k) => !isRowHandleKey(k)),
      );

      let same = nextHidden.size === hiddenCols.size;
      if (same) {
        for (const k of nextHidden) {
          if (!hiddenCols.has(k)) {
            same = false;
            break;
          }
        }
      }

      if (!same) {
        hiddenCols.clear();
        nextHidden.forEach((k) => hiddenCols.add(k));
        changed = true;
      }
    }

    if (ctx.rowMenuEnabled) hiddenCols.delete(ctx.ROW_HANDLE_KEY);
    return changed;
  }

  function squelchHeaderClick(ms = 220) {
    const until = Date.now() + Math.max(0, Number(ms) || 0);
    ctx._teSquelchHeaderClickUntil = Math.max(Number(ctx._teSquelchHeaderClickUntil || 0), until);
  }

  function setColumnWidth(colKey, width, { persist = true, notify = true } = {}) {
    if (!colKey || !canResizeCol(colKey)) return false;

    const col = colForKey(colKey);
    if (!col) return false;

    const w = clampWidthForCol(col, width);
    const prev = widthFromCol(col);
    const changed = !(prev != null && Math.abs(prev - w) < 0.5);
    col.width = w;

    const cg = ctx.table?.querySelector?.("colgroup");
    if (cg) {
      const colEl = cg.querySelector(`col[data-col-key="${escCss(colKey)}"]`);
      if (colEl) colEl.style.width = `${w}px`;
    }

    if (changed && notify) ctx.scheduleNotifyRendered?.();
    if (changed && persist) saveLayoutProfile();
    return changed;
  }

  function remapCellSelection(beforeOrder) {
    const s = ctx._sel;
    if (!s || !Array.isArray(beforeOrder)) return;

    if (s.cellAnchor && Number.isInteger(s.cellAnchor.c)) {
      const oldKey = beforeOrder[s.cellAnchor.c] || null;
      const c = oldKey ? (ctx.colIndexByKey?.(oldKey) ?? -1) : -1;
      if (c >= 0) s.cellAnchor = { r: s.cellAnchor.r, c };
      else s.cellAnchor = null;
    }

    if (s.cellFocus && Number.isInteger(s.cellFocus.c)) {
      const oldKey = beforeOrder[s.cellFocus.c] || null;
      const c = oldKey ? (ctx.colIndexByKey?.(oldKey) ?? -1) : -1;
      if (c >= 0) s.cellFocus = { r: s.cellFocus.r, c };
      else s.cellFocus = null;
    }

    if (s.cellRange && Number.isInteger(s.cellRange.c0) && Number.isInteger(s.cellRange.c1)) {
      const lo = Math.max(0, Math.min(s.cellRange.c0, s.cellRange.c1));
      const hi = Math.max(0, Math.max(s.cellRange.c0, s.cellRange.c1));
      const keys = beforeOrder.slice(lo, hi + 1).filter(Boolean);
      const idxs = keys.map((k) => ctx.colIndexByKey?.(k) ?? -1).filter((i) => i >= 0);

      if (!idxs.length) {
        s.cellRange = null;
      } else {
        s.cellRange = {
          r0: s.cellRange.r0,
          r1: s.cellRange.r1,
          c0: Math.min(...idxs),
          c1: Math.max(...idxs),
        };
      }
    }

    if (!s.cellAnchor && s.cellFocus) s.cellAnchor = { ...s.cellFocus };
    if (!s.cellAnchor) s.cellRange = null;
  }

  function snapshotCurrentRows() {
    const rows = ctx.getRowEls?.() || [];
    return rows.map((tr) => {
      if (typeof ctx.snapshotRowItem === "function") return ctx.snapshotRowItem(tr);
      return ctx.readRow?.(tr, { includeOutputs: true }) || {};
    });
  }

  function rerenderColumnsWithRows(rowItems, { remapFromOrder = null } = {}) {
    ctx.renderColgroup?.();
    ctx.renderHeader?.();
    ctx.setItems?.(rowItems);

    if (Array.isArray(remapFromOrder)) remapCellSelection(remapFromOrder);
    ctx.applySelectionVisuals?.();
    ctx.scheduleNotifyRendered?.();
  }

  function applyColumnOrder(orderSpec, { persist = true } = {}) {
    if (!Array.isArray(orderSpec) || !orderSpec.length) return false;

    const beforeOrder = cols.map((c) => c?.key || "");
    const rowItems = snapshotCurrentRows();
    const looksLikeRefs = orderSpec.some((x) => x && typeof x === "object");
    const changed = looksLikeRefs
      ? reorderColumnsByRefs(orderSpec)
      : reorderColumnsByOrder(orderSpec);
    if (!changed) return false;

    withHistorySquelch(() => {
      rerenderColumnsWithRows(rowItems, { remapFromOrder: beforeOrder });
    });
    if (persist) saveLayoutProfile();
    return true;
  }

  function moveColumnByKey(
    colKey,
    targetKey,
    { place = "before", persist = true, notify = true, recordHistory = true } = {},
  ) {
    if (!colKey || !targetKey) return false;
    if (colKey === targetKey) return false;
    if (!canReorderCol(colKey)) return false;
    if (!canReorderCol(targetKey)) return false;

    const from = cols.findIndex((c) => c?.key === colKey);
    const target = cols.findIndex((c) => c?.key === targetKey);
    if (from < 0 || target < 0) return false;

    const beforeOrder = cols.map((c) => c?.key || "");
    const beforeCols = cols.slice();
    const rowItems = snapshotCurrentRows();

    let insertAt = target + (place === "after" ? 1 : 0);
    if (insertAt > from) insertAt -= 1;

    if (ctx.rowMenuEnabled && cols[0]?.key === ctx.ROW_HANDLE_KEY) {
      insertAt = Math.max(1, insertAt);
    }
    if (insertAt === from) return false;

    const [moved] = cols.splice(from, 1);
    cols.splice(insertAt, 0, moved);

    const afterOrder = cols.map((c) => c?.key || "");
    const afterCols = cols.slice();
    withHistorySquelch(() => {
      rerenderColumnsWithRows(rowItems, { remapFromOrder: beforeOrder });
    });

    if (notify) {
      const col = colForKey(colKey);
      const label = String(col?.header || col?.key || colKey);
      ctx.notify?.ok?.(`Moved column: ${label}`);
    }

    if (persist) saveLayoutProfile();
    if (recordHistory) {
      pushHistoryAction({
        type: "columns-reorder",
        label: "move-column",
        undo: () => {
          applyColumnOrder(beforeCols, { persist });
        },
        redo: () => {
          applyColumnOrder(afterCols, { persist });
        },
      });
    }
    return true;
  }

  function resetLayoutProfile({ notify = true } = {}) {
    clearLayoutProfile();

    const beforeOrder = cols.map((c) => c?.key || "");
    const rowItems = snapshotCurrentRows();

    cols.forEach((col) => {
      if (!col?.key || isRowHandleKey(col.key)) return;
      if (Object.prototype.hasOwnProperty.call(baselineProfile.widths, col.key)) return;
      try {
        delete col.width;
      } catch {
        col.width = undefined;
      }
    });

    applyLayoutProfile(baselineProfile);
    withHistorySquelch(() => {
      rerenderColumnsWithRows(rowItems, { remapFromOrder: beforeOrder });
    });

    if (notify) ctx.notify?.ok?.("Reset table layout profile.");
    return true;
  }

  function wireResize() {
    if (!resizeEnabled || !ctx.thead) return;

    let active = null;
    let moveHandler = null;
    let endHandler = null;

    function cleanupDrag() {
      if (moveHandler) document.removeEventListener("pointermove", moveHandler, true);
      if (endHandler) {
        document.removeEventListener("pointerup", endHandler, true);
        document.removeEventListener("pointercancel", endHandler, true);
      }
      moveHandler = null;
      endHandler = null;
      active = null;
      ctx.table?.classList?.remove?.("te-col-resizing");
      try {
        document.body.classList.remove("te-col-resizing");
      } catch {}
    }

    ctx.addCleanup?.(cleanupDrag);

    const onPointerDown = (e) => {
      const grip = e.target?.closest?.(".te-col-resizer");
      if (!grip || !ctx.thead.contains(grip)) return;

      const th = grip.closest("th[data-col-key]");
      const colKey = th?.dataset?.colKey;
      if (!colKey || !canResizeCol(colKey)) return;

      e.preventDefault();
      e.stopPropagation();
      squelchHeaderClick(260);

      const colEl = ctx.table?.querySelector?.(`colgroup col[data-col-key="${escCss(colKey)}"]`);
      const rect = colEl?.getBoundingClientRect?.() || th?.getBoundingClientRect?.();
      const startWidth = rect && Number.isFinite(rect.width) ? rect.width : 120;

      active = {
        colKey,
        startX: Number(e.clientX || 0),
        startWidth,
      };

      ctx.table?.classList?.add?.("te-col-resizing");
      try {
        document.body.classList.add("te-col-resizing");
      } catch {}

      moveHandler = (ev) => {
        if (!active) return;
        const x = Number(ev.clientX || 0);
        const dx = x - active.startX;
        const next = active.startWidth + dx;
        setColumnWidth(active.colKey, next, { persist: false, notify: false });
      };

      endHandler = () => {
        if (active) saveLayoutProfile();
        ctx.scheduleNotifyRendered?.();
        cleanupDrag();
      };

      document.addEventListener("pointermove", moveHandler, true);
      document.addEventListener("pointerup", endHandler, true);
      document.addEventListener("pointercancel", endHandler, true);
    };

    ctx.on?.(ctx.thead, "pointerdown", onPointerDown, true);
  }

  function wireReorder() {
    if (!reorderEnabled || !ctx.thead) return;

    let drag = null;

    function clearDropClasses() {
      ctx.thead.querySelectorAll("th.te-drop-before, th.te-drop-after, th.te-col-dragging").forEach((th) => {
        th.classList.remove("te-drop-before", "te-drop-after", "te-col-dragging");
      });
      ctx.table?.classList?.remove?.("te-col-reordering");
    }

    const onDragStart = (e) => {
      const th = e.target?.closest?.("th[data-col-key]");
      if (!th || !ctx.thead.contains(th)) return;
      if (e.target?.closest?.(".te-col-resizer")) {
        e.preventDefault();
        return;
      }

      const colKey = th.dataset?.colKey;
      if (!colKey || !canReorderCol(colKey)) {
        e.preventDefault();
        return;
      }

      drag = {
        fromKey: colKey,
        dropKey: null,
        place: "before",
      };

      th.classList.add("te-col-dragging");
      ctx.table?.classList?.add?.("te-col-reordering");

      try {
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", colKey);
        }
      } catch {}
    };

    const onDragOver = (e) => {
      if (!drag) return;

      const th = e.target?.closest?.("th[data-col-key]");
      if (!th || !ctx.thead.contains(th)) return;

      const targetKey = th.dataset?.colKey;
      if (!targetKey || targetKey === drag.fromKey) return;
      if (!canReorderCol(targetKey)) return;

      e.preventDefault();

      const rect = th.getBoundingClientRect();
      const x = Number(e.clientX || 0) - rect.left;
      const place = x >= rect.width / 2 ? "after" : "before";

      clearDropClasses();
      th.classList.add(place === "after" ? "te-drop-after" : "te-drop-before");
      ctx.table?.classList?.add?.("te-col-reordering");

      drag.dropKey = targetKey;
      drag.place = place;
    };

    const onDrop = (e) => {
      if (!drag) return;
      e.preventDefault();

      const fromKey = drag.fromKey;
      const dropKey = drag.dropKey;
      const place = drag.place;

      clearDropClasses();
      drag = null;

      if (!fromKey || !dropKey) return;
      const moved = moveColumnByKey(fromKey, dropKey, { place, persist: true, notify: true });
      if (moved) {
        squelchHeaderClick(260);
        try {
          ctx.table?.focus?.({ preventScroll: true });
        } catch {
          try {
            ctx.table?.focus?.();
          } catch {}
        }
      }
    };

    const onDragEnd = () => {
      clearDropClasses();
      drag = null;
    };

    ctx.on?.(ctx.thead, "dragstart", onDragStart);
    ctx.on?.(ctx.thead, "dragover", onDragOver);
    ctx.on?.(ctx.thead, "drop", onDrop);
    ctx.on?.(ctx.thead, "dragend", onDragEnd);
  }

  ctx._layoutProfileKey = storageKey;
  ctx.isColumnResizeEnabledForKey = canResizeCol;
  ctx.isColumnReorderEnabledForKey = canReorderCol;
  ctx.setColumnWidth = setColumnWidth;
  ctx.moveColumnByKey = moveColumnByKey;
  ctx.persistLayoutProfile = saveLayoutProfile;
  ctx.clearLayoutProfile = clearLayoutProfile;
  ctx.resetLayoutProfile = resetLayoutProfile;

  const loaded = loadLayoutProfile();
  if (loaded) applyLayoutProfile(loaded);

  wireResize();
  wireReorder();
}
